// Projects, areas and sections, as stored in `projects/{id}`, `areas/{id}` and `sections/{id}`
// (Models.swift: Project, ProjectKind, ProjectLayout, Area, TodoSection), and the Inbox's traces (InboxHistory.swift).

import { storedDay } from "../days";
import { newID } from "../ids";
import type { Day, RepeatRule, Start } from "../types";
import { encodeRepeatRule, encodeStart, readRecord, readRepeatRule, readStart } from "./shared";
import {
  decoding, document, readEnum, readMap, readString, storedFields, withoutUndefined,
  type DecodeContext, type Decoded, type EncodeOptions, type Fields, type ValueReader,
} from "./values";

// MARK: - Project

/**
 * A project has an end; a list goes on; the birthdays list is the one list of birthdays. A kind from a newer version
 * is kept as stored (`{unknown: value}`) and treated like a plain project, so its to-dos stay in it.
 */
export type ProjectKind = "project" | "list" | "birthdays" | { unknown: string };

export type ProjectLayout = "list" | "board";

export interface Project {
  id: string;
  title: string;
  notes: string;
  areaID?: string;
  order: number;
  /** When work on the project begins. Absent: active now. */
  start?: Start;
  /** A day. */
  deadline?: Day;
  completedAt?: Date;
  layout?: ProjectLayout;
  /** Absent, as in older data, is a project. */
  kind?: ProjectKind;
  /** Switched off: not on Home, makes nothing on its own (the birthdays list). */
  isOff?: boolean;
  /** The birthdays list only: false when birthdays make no to-do on the day. */
  makesBirthdayTodos?: boolean;
  tags?: string[];
  isSample?: boolean;
  /** A project that comes back when it is completed. */
  repeats?: RepeatRule;
  /** Made from a template (docs/45). */
  templateID?: string;
  /** Which of its to-dos came from which step: to-do id to step id. */
  templateSteps?: Record<string, string>;
  unknownFields: Fields;
}

export const PROJECT_FIELDS = [
  "id", "title", "notes", "areaID", "order", "start", "deadline", "completedAt", "layout", "kind", "isOff",
  "makesBirthdayTodos", "tags", "isSample", "repeats", "templateID", "templateSteps",
] as const;

export function makeProject(values: Partial<Project> & { title: string }): Project {
  return { id: newID(), notes: "", order: 0, unknownFields: {}, ...values };
}

/** Any string is a kind: one this version doesn't know is kept (Models.swift: ProjectKind). */
const readProjectKind: ValueReader<ProjectKind> = (value, path, context) => {
  const kind = readString(value, path, context);
  return kind === "project" || kind === "list" || kind === "birthdays" ? kind : { unknown: kind };
};

export function encodeProjectKind(kind: ProjectKind): string {
  return typeof kind === "string" ? kind : kind.unknown;
}

/** The birthdays list counts as a list too. */
export function isList(project: Project): boolean {
  return project.kind === "list" || project.kind === "birthdays";
}

export function decodeProject(data: Fields, context: DecodeContext): Decoded<Project> {
  return decoding(() => readRecord(data, context, (fields) => withoutUndefined<Project>({
    id: fields.uuid("id"),
    title: fields.string("title"),
    notes: fields.string("notes"),
    areaID: fields.optionalUUID("areaID"),
    order: fields.number("order"),
    start: fields.optional("start", readStart),
    deadline: fields.optionalDay("deadline"),
    completedAt: fields.optionalMoment("completedAt"),
    layout: fields.optional("layout", readEnum(["list", "board"] as const)),
    kind: fields.optional("kind", readProjectKind),
    isOff: fields.optionalBool("isOff"),
    makesBirthdayTodos: fields.optionalBool("makesBirthdayTodos"),
    tags: fields.optionalStrings("tags"),
    isSample: fields.optionalBool("isSample"),
    repeats: fields.optional("repeats", readRepeatRule),
    templateID: fields.optionalUUID("templateID"),
    templateSteps: fields.optional("templateSteps", readMap(readString)),
    unknownFields: fields.unknownFields(PROJECT_FIELDS),
  })));
}

export function encodeProject(project: Project, options?: EncodeOptions): Fields {
  return storedFields(document(project.unknownFields, {
    id: project.id,
    title: project.title,
    notes: project.notes,
    areaID: project.areaID,
    order: project.order,
    start: project.start && encodeStart(project.start),
    deadline: project.deadline === undefined ? undefined : storedDay(project.deadline),
    completedAt: project.completedAt,
    layout: project.layout,
    kind: project.kind === undefined ? undefined : encodeProjectKind(project.kind),
    isOff: project.isOff,
    makesBirthdayTodos: project.makesBirthdayTodos,
    tags: project.tags && [...project.tags],
    isSample: project.isSample,
    repeats: project.repeats && encodeRepeatRule(project.repeats),
    templateID: project.templateID,
    templateSteps: project.templateSteps && { ...project.templateSteps },
  }), options);
}

// MARK: - Area

export interface Area {
  id: string;
  title: string;
  order: number;
  isCollapsed: boolean;
  tags?: string[];
  isSample?: boolean;
  unknownFields: Fields;
}

export const AREA_FIELDS = ["id", "title", "order", "isCollapsed", "tags", "isSample"] as const;

export function makeArea(values: Partial<Area> & { title: string }): Area {
  return { id: newID(), order: 0, isCollapsed: false, unknownFields: {}, ...values };
}

export function decodeArea(data: Fields, context: DecodeContext): Decoded<Area> {
  return decoding(() => readRecord(data, context, (fields) => withoutUndefined<Area>({
    id: fields.uuid("id"),
    title: fields.string("title"),
    order: fields.number("order"),
    isCollapsed: fields.bool("isCollapsed"),
    tags: fields.optionalStrings("tags"),
    isSample: fields.optionalBool("isSample"),
    unknownFields: fields.unknownFields(AREA_FIELDS),
  })));
}

export function encodeArea(area: Area, options?: EncodeOptions): Fields {
  return storedFields(document(area.unknownFields, {
    id: area.id, title: area.title, order: area.order, isCollapsed: area.isCollapsed,
    tags: area.tags && [...area.tags], isSample: area.isSample,
  }), options);
}

// MARK: - Section

/** A heading in a project and a column on its board; without a project, a section of Today. */
export interface Section {
  id: string;
  title: string;
  projectID?: string;
  order: number;
  isCollapsed?: boolean;
  /** Today only: the section that holds the to-dos placed in no section. */
  isDefault?: boolean;
  isSample?: boolean;
  unknownFields: Fields;
}

export const SECTION_FIELDS = ["id", "title", "projectID", "order", "isCollapsed", "isDefault", "isSample"] as const;

export function makeSection(values: Partial<Section> & { title: string }): Section {
  return { id: newID(), order: 0, unknownFields: {}, ...values };
}

export function decodeSection(data: Fields, context: DecodeContext): Decoded<Section> {
  return decoding(() => readRecord(data, context, (fields) => withoutUndefined<Section>({
    id: fields.uuid("id"),
    title: fields.string("title"),
    projectID: fields.optionalUUID("projectID"),
    order: fields.number("order"),
    isCollapsed: fields.optionalBool("isCollapsed"),
    isDefault: fields.optionalBool("isDefault"),
    isSample: fields.optionalBool("isSample"),
    unknownFields: fields.unknownFields(SECTION_FIELDS),
  })));
}

export function encodeSection(section: Section, options?: EncodeOptions): Fields {
  return storedFields(document(section.unknownFields, {
    id: section.id, title: section.title, projectID: section.projectID, order: section.order,
    isCollapsed: section.isCollapsed, isDefault: section.isDefault, isSample: section.isSample,
  }), options);
}

// MARK: - InboxTrace

/** What is left of an Inbox item deleted before it was decided: when it came and when it went. Keyed by the to-do's id. */
export interface InboxTrace {
  id: string;
  createdAt: Date;
  leftAt: Date;
  unknownFields: Fields;
}

export const INBOX_TRACE_FIELDS = ["id", "createdAt", "leftAt"] as const;

export function decodeInboxTrace(data: Fields, context: DecodeContext): Decoded<InboxTrace> {
  return decoding(() => readRecord(data, context, (fields) => ({
    id: fields.uuid("id"),
    createdAt: fields.moment("createdAt"),
    leftAt: fields.moment("leftAt"),
    unknownFields: fields.unknownFields(INBOX_TRACE_FIELDS),
  })));
}

export function encodeInboxTrace(trace: InboxTrace, options?: EncodeOptions): Fields {
  return storedFields(document(trace.unknownFields, { id: trace.id, createdAt: trace.createdAt, leftAt: trace.leftAt }), options);
}
